import * as React from 'react';
import { useQuery } from '@apollo/client';
import {
    GET_NODE_STATUS,
    TGetNodeStatusOutput
} from '../../../../../graphql/schema';
import DataValue from './dataValue';
import { Control, General, Battery100, Map } from '../../../../../components/Icons';

interface IStatusProps {
    nodeId: string;
}

enum NodeStatus {
    POWER_OFF = 0,
    POWER_ON = 1,
}

const Status: React.FunctionComponent<IStatusProps> = ({ nodeId }) => {
    // Query node status with polling for real-time updates
    const { data: queryData, loading, error } = useQuery<TGetNodeStatusOutput>(
        GET_NODE_STATUS,
        {
            variables: {
                nodeId: nodeId
            },
            skip: !nodeId,
            pollInterval: 5000, // Poll every 5 seconds for real-time updates
            fetchPolicy: 'cache-and-network'
        }
    );

    const nodeStatus = queryData?.getNodeStatus || null;

    // Helper function to determine status based on values
    const getStatusForValue = (value: any, type: string) => {
        if (value === null || value === undefined) return 'normal';
        
        switch (type) {
            case 'ctValue':
                return value > 100 ? 'warning' : 'success';
            case 'ptValue':
                return value > 50 ? 'warning' : 'success';
            case 'dimLevel':
                return value < 20 ? 'warning' : value > 80 ? 'error' : 'success';
            case 'status':
                if (value === 0) return 'success';
                return value === 'ONLINE' ? 'success' : value === 'OFFLINE' ? 'error' : 'warning';
            default:
                return 'normal';
        }
    };

    // Helper function to format display value
    const formatDisplayValue = (value: any, type: string) => {
        if (value === null || value === undefined) return 'N/A';
        
        switch (type) {
            case 'status':
                if (value === NodeStatus.POWER_OFF) return 'Power Off';
                if (value === NodeStatus.POWER_ON) return 'Power On';
                return value;
            default:
                return value;
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="grid grid-cols-2 gap-4">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="h-20 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="text-center py-8">
                    <div className="text-red-500 mb-2">
                        <General size="lg" />
                    </div>
                    <p className="text-red-600 font-medium">Lỗi khi tải dữ liệu</p>
                    <p className="text-gray-500 text-sm mt-1">Vui lòng thử lại sau</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-white">
            <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Trạng thái Node
                </h3>
                <p className="text-sm text-gray-500">
                    Thông tin cập nhật theo thời gian thực
                </p>
            </div>

            {nodeStatus ? (
                <div className="space-y-6">
                    <div className="flex flex-wrap grid-cols-1 md:grid-cols-2 gap-4">
                        <DataValue
                            label="CT Value"
                            value={nodeStatus.ctValue}
                            unit="mA"
                            status={getStatusForValue(nodeStatus.ctValue, 'ctValue')}
                            icon={<Control size="sm" />}
                        />
                        <DataValue
                            label="PT Value"
                            value={nodeStatus.ptValue}
                            unit="V"
                            status={getStatusForValue(nodeStatus.ptValue, 'ptValue')}
                            icon={<Battery100 size="sm" />}
                        />
                        <DataValue
                            label="Dim Level"
                            value={nodeStatus.dimLevel}
                            unit="%"
                            status={getStatusForValue(nodeStatus.dimLevel, 'dimLevel')}
                            icon={<General size="sm" />}
                        />
                        <DataValue
                            label="Status"
                            value={formatDisplayValue(nodeStatus.status, 'status')}
                            status={getStatusForValue(nodeStatus.status, 'status')}
                            icon={<Map size="sm" />}
                        />
                    </div>
                </div>
            ) : (
                <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                        <General size="lg" />
                    </div>
                    <p className="text-gray-500 font-medium">Đang chờ dữ liệu cập nhật...</p>
                    <p className="text-gray-400 text-sm mt-1">
                        Hệ thống đang kết nối và đồng bộ dữ liệu
                    </p>
                </div>
            )}
        </div>
    );
};

export default Status;
